import { useEffect, useState } from "react";
import { Avatar, Box, Collapse, Tooltip, TextField, Typography } from "@mui/material";

import { StyledSwitch } from "../../ReusableComponents/StyledSwitch";
import StyledButton from "../../ReusableComponents/Button";
import {
  GetAutoCommentSettingsService,
  GetAutoCommentQueueService,
  ToggleAutoCommentService,
  ApplyAutoCommentRulesService,
} from "../../../../Services/AutoCommentService";
import { mixpanelEvent } from "../../../../Utils/mixpanelUtils";
import { eligibleAgainLabel, queueTimeLabel, skippedSummary, SKIP_REASON_LABELS } from "./autoCommentQueueLabels";

const UP_NEXT_LIMIT = 10;

/** Renders a UTC hour (as stored) back in the user's own local time for display. */
function formatLocalStartHour(utcHour, tz) {
  if (utcHour === null || utcHour === undefined) return null;
  try {
    const now = new Date();
    const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHour, 0, 0));
    return new Intl.DateTimeFormat([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: tz || "UTC",
    }).format(utcDate);
  } catch {
    return `${utcHour}:00 UTC`;
  }
}

function prospectName(item) {
  const name = `${item.prospect?.first_name || ""} ${item.prospect?.last_name || ""}`.trim();
  return name || "a monitored customer";
}

/** One sentence on what Agent Mode is doing right now, from the queue projection's status. */
function queueStatusText(queue) {
  const dryRun = queue.dry_run ? " (dry run)" : "";
  const next = queue.queue[0];
  switch (queue.status) {
    case "scheduled":
      return `Next comment ${queueTimeLabel(next.estimated_at, true)} on ${prospectName(next)}'s post${dryRun}.`;
    case "cap_reached":
      return `Today's limit of ${queue.daily_cap} reached. Next comment ${queueTimeLabel(next.estimated_at, true)} on ${prospectName(next)}'s post${dryRun}.`;
    case "empty":
      return "Nothing queued right now. Agent Mode will comment when your customers post something new.";
    case "no_cookie":
    case "cookie_invalid":
      return "Paused: reconnect LinkedIn by opening it in Chrome with the Kynzo extension.";
    case "plan_lapsed":
      return "Paused: upgrade your plan to keep Agent Mode commenting.";
    default:
      return null;
  }
}

function QueueRow({ item, label }) {
  return (
    <Box display="flex" alignItems="center" gap="8px" sx={{ minWidth: 0 }}>
      <Avatar sx={{ height: "24px", width: "24px" }} src={item.prospect?.profile_photo_url} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 600 }} noWrap>
          {prospectName(item)}
        </Typography>
        <Typography
          component="a"
          href={item.post_link}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "12px", color: "var(--color-grey-500)", display: "block", textDecoration: "none" }}
          noWrap
        >
          {item.snippet}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "12px", color: "var(--color-grey-700)", whiteSpace: "nowrap" }}>{label}</Typography>
    </Box>
  );
}

/** A heading that expands/collapses the rows under it. */
function CollapsibleSection({ title, open, onToggle, children }) {
  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Typography
        component="button"
        onClick={onToggle}
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          textAlign: "left",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        {open ? "▾" : "▸"} {title}
      </Typography>
      <Collapse in={open}>
        <Box display="flex" flexDirection="column" gap="8px">
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * "Agent Mode" for the customer feed - toggles automatic AI-generated commenting on new posts, and
 * lets the user restate the pacing rules (daily cap, active hours, repeat-contact cooldown) in
 * plain English instead of a settings form. The free-text prompt is interpreted server-side via a
 * fixed, narrow set of tool calls (see kynzo-server's ApplyAutoCommentRules) - this component just
 * displays whatever the backend says actually changed.
 */
const AutoCommentPanel = ({ premiumUser }) => {
  const [settings, setSettings] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [queue, setQueue] = useState(null);
  const [showUpNext, setShowUpNext] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  useEffect(() => {
    if (!premiumUser) return;
    GetAutoCommentSettingsService()
      .then((res) => res.json())
      .then((data) => setSettings(data))
      .catch(() => {});
  }, [premiumUser]);

  // Refetched whenever the settings change, since the window, cap and cooldown all move the queue.
  useEffect(() => {
    if (!settings?.enabled) {
      setQueue(null);
      return;
    }
    GetAutoCommentQueueService()
      .then((res) => res.json())
      .then((data) => setQueue(data))
      .catch(() => {});
  }, [settings]);

  const handleToggle = (e) => {
    const enabled = e.target.checked;
    setIsToggling(true);
    ToggleAutoCommentService(enabled)
      .then((res) => res.json())
      .then((data) => {
        setSettings(data);
        mixpanelEvent("auto_comment_toggled", { enabled });
      })
      .catch(() => {})
      .finally(() => setIsToggling(false));
  };

  const handleApplyRules = () => {
    if (!promptText.trim() || isApplying) return;
    setIsApplying(true);
    setFeedback(null);
    ApplyAutoCommentRulesService(promptText.trim())
      .then((res) => res.json())
      .then((data) => {
        setSettings(data.settings || settings);
        setFeedback(data);
        mixpanelEvent("auto_comment_rules_applied", { applied_count: (data.applied || []).length });
        setPromptText("");
      })
      .catch(() => setFeedback({ applied: [], message: "Something went wrong, please try again." }))
      .finally(() => setIsApplying(false));
  };

  if (!settings && premiumUser) {
    return null; // avoid a flash of the toggle before we know the current state
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        padding: "14px 16px",
        borderRadius: "12px",
        border: "1px solid #000",
        background: "var(--color-grey-0)",
      }}
    >
      <Box display="flex" alignItems="center" gap="10px">
        <Tooltip title={premiumUser ? "" : "Upgrade to unlock Agent Mode"} placement="top">
          <span>
            <StyledSwitch
              checked={!!settings?.enabled}
              disabled={!premiumUser || isToggling}
              onChange={handleToggle}
            />
          </span>
        </Tooltip>
        <Typography sx={{ fontWeight: 600 }}>Agent Mode</Typography>
        <Typography sx={{ color: "var(--color-grey-500)", fontSize: "14px" }}>
          Comments on new posts from your monitored customers — like a real person would, never on
          reposts or reshares
        </Typography>
      </Box>

      {settings?.enabled && (
        <>
          <Typography sx={{ color: "var(--color-grey-500)", fontSize: "13px" }}>
            Currently: commenting on monitored customers&apos; original posts, paced like natural
            activity — up to {settings.daily_cap} a day over about {settings.window_hours} hours
            starting around {formatLocalStartHour(settings.window_start_hour_utc, settings.timezone)}{" "}
            your time, never the same person twice within {settings.repeat_cooldown_days} day
            {settings.repeat_cooldown_days === 1 ? "" : "s"}, and only on posts from the last{" "}
            {settings.max_post_age_days} day{settings.max_post_age_days === 1 ? "" : "s"}.
          </Typography>
          <Box display="flex" gap="8px" alignItems="flex-start">
            <TextField
              multiline
              minRows={1}
              maxRows={3}
              fullWidth
              placeholder='e.g. "auto comment 80 contacts per day for 8 hours every day, never comment the same person more than once a week"'
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              disabled={isApplying}
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: "40px",
                  alignItems: "center",
                  paddingTop: "10px !important",
                  paddingBottom: "10px !important",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000",
                    borderWidth: "1px",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  paddingTop: "0px !important",
                  paddingBottom: "0px !important",
                  lineHeight: "20px !important",
                },
              }}
            />
            <StyledButton onClick={handleApplyRules} disabled={isApplying || !promptText.trim()}>
              {isApplying ? "Applying..." : "Apply"}
            </StyledButton>
          </Box>
          <Typography sx={{ color: "var(--color-grey-500)", fontSize: "12px" }}>
            You can set: comments per day, active hours and start time, how long to wait before
            commenting on the same person again, and how old a post can be (e.g. &quot;only posts from
            the last 2 days&quot;).
          </Typography>
          {queue && queueStatusText(queue) && (
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{queueStatusText(queue)}</Typography>
          )}
          {queue?.queue?.length > 0 && (
            <CollapsibleSection
              title={`Up next (${queue.queue.length})`}
              open={showUpNext}
              onToggle={() => setShowUpNext((open) => !open)}
            >
              {queue.queue.slice(0, UP_NEXT_LIMIT).map((item) => (
                <QueueRow
                  key={item.submission_id}
                  item={item}
                  label={queueTimeLabel(item.estimated_at, item.is_next)}
                />
              ))}
              <Typography sx={{ color: "var(--color-grey-500)", fontSize: "12px" }}>
                Newest posts go first, so new posts can move ahead in line.
              </Typography>
            </CollapsibleSection>
          )}
          {queue?.skipped?.length > 0 && (
            <CollapsibleSection
              title={`Not queued (${queue.skipped.length}): ${skippedSummary(queue.skipped)}`}
              open={showSkipped}
              onToggle={() => setShowSkipped((open) => !open)}
            >
              {queue.skipped.map((item) => (
                <QueueRow
                  key={item.submission_id}
                  item={item}
                  label={[SKIP_REASON_LABELS[item.reason], eligibleAgainLabel(item.eligible_again_at)]
                    .filter(Boolean)
                    .join("; ")}
                />
              ))}
            </CollapsibleSection>
          )}
          {feedback && (
            <Typography sx={{ fontSize: "13px", color: "var(--color-grey-700)" }}>
              {feedback.applied && feedback.applied.length > 0
                ? feedback.applied
                    .map((change) => `Set ${change.setting} to ${String(change.new_value)}`)
                    .join(". ") + "."
                : feedback.message || "No settings changed."}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default AutoCommentPanel;
